// external
import { useContext, useCallback, memo } from "react";
import PropTypes from "prop-types";

// components
import CustomTextInput from "components/partials/custom-text-input/custom-text-input";

// state managing/secuiry
import { FaoRegimeContext } from "context/FaoRegimeContext";

// data/constants
import { ATTRIBUTE_OPTIONS } from "../constants/attribute-options.constants";
import { CELL_METRICS } from "../constants/cell-metrics.constants";

// css/styling
import { STYLES } from "../constants/styles.constants";

// assets
import Icons from "assets/icons";

const CellMetric = memo(
  ({ metric, rowIndex, colIndex, value, isEditable, onChange }) => {
    const metricData = CELL_METRICS.find((item) => item.value === metric[0]);
    const { unitBefore, unitAfter, max, min } = metricData;

    return (
      <div className="flex">
        {unitBefore && (
          <span className="py-2.5 pl-1 text-sm">{unitBefore}</span>
        )}
        <CustomTextInput
          type="number"
          placeholder="Value"
          inputWrapperCustomClass="flex-shrink max-w-[80px]"
          value={value}
          inputCustomClass="border-none text-sm"
          onChange={(event) =>
            onChange(
              rowIndex,
              colIndex,
              metric[0],
              event.target.value,
              max,
              min
            )
          }
          disabled={!isEditable}
        />
        {unitAfter && <span className="py-2.5 pr-1 text-sm">{unitAfter}</span>}
      </div>
    );
  }
);

CellMetric.propTypes = {
  metric: PropTypes.array.isRequired,
  rowIndex: PropTypes.number.isRequired,
  colIndex: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  isEditable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const DimensionHeader = memo(({ attribute, rangeStart, rangeEnd }) => {
  const { unit, type } = attribute;
  return (
    <span className="w-auto">
      {`${unit}${rangeStart}${type === "range" ? `-${rangeEnd}` : ``}`}
    </span>
  );
});

DimensionHeader.propTypes = {
  attribute: PropTypes.shape({
    unit: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  rangeStart: PropTypes.string.isRequired,
  rangeEnd: PropTypes.string.isRequired,
};

function RegimeCellViewer({
  matrixInd,
  isEditable,
  isDisplay,
  curMatrix,
  cellMetrics,
}) {
  const {
    dimensions_x,
    dimensions_y,
    selectedX,
    selectedY,
    matrices,
    setMatrices,
    currentlyEditing,
  } = useContext(FaoRegimeContext);
  const attribute_x = ATTRIBUTE_OPTIONS.find(
    (option) => option.value === selectedX
  );
  const attribute_y = ATTRIBUTE_OPTIONS.find(
    (option) => option.value === selectedY
  );
  const onChangeCellValue = useCallback(
    (row_index, col_index, metric, value, max, min) => {
      if (max && value > max) {
        value = max;
      }
      if (min && value < min) {
        value = min;
      }
      const updatedMatrices = JSON.parse(JSON.stringify(matrices));
      const currentMatrix = updatedMatrices[currentlyEditing];
      currentMatrix[metric][row_index][col_index] = value;
      setMatrices(updatedMatrices);
    },
    [matrices, currentlyEditing, setMatrices]
  );

  const getCellValue = useCallback(
    (metric, row_index, col_index) => {
      return isDisplay
        ? curMatrix[metric[0]][row_index][col_index]
        : matrices[matrixInd][metric[0]][row_index][col_index];
    },
    [isDisplay, curMatrix, matrices, matrixInd]
  );

  return (
    <div className="mt-5 w-[500px]">
      <div className="align-center">
        <legend className={STYLES.label}></legend>
      </div>
      <div className="mb-2">
        <div className="flex">
          <Icons.Warning customClass="h-3 w-3 text-red-500 mr-1 mt-1" />
          <p className="text-sm text-red-300">
            Negative values are not allowed
          </p>
        </div>
        <div className="flex">
          <Icons.Info customClass="h-3 w-3 text-green-500 mr-1 mt-1" />
          <p className="text-sm text-green-500 text-opacity-50">
            Percentages are between 1 and 100
          </p>
        </div>
      </div>
      <table className="table-fixed rounded-md border">
        <thead>
          <tr>
            <th colSpan="100%" className="border">
              <h3 className="font-family py-2 text-lg text-blue-03">
                {attribute_x.label} x {attribute_y.label}
              </h3>
            </th>
          </tr>
          <tr>
            <th className="rounded-tl-md border-x border-y"></th>
            {dimensions_x.map((item) => (
              <th
                key={`${item[0]}-${item[1]}`}
                colSpan={Object.keys(cellMetrics).length}
                className="font-family text-md border-b border-r px-4 py-2 font-semibold text-gray-05"
              >
                <DimensionHeader
                  attribute={attribute_x}
                  rangeStart={item[0]}
                  rangeEnd={item[1]}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border-x"></td>
            {dimensions_x.map((_, xIndex) =>
              Object.entries(cellMetrics).map(([metricKey], metricIndex) => {
                const metricData = CELL_METRICS.find(
                  (item) => item.value === metricKey
                );
                return (
                  <td
                    key={`${xIndex}-${metricIndex}`}
                    className="border-r p-2 text-sm font-semibold text-gray-05"
                  >
                    {metricData.label}
                  </td>
                );
              })
            )}
          </tr>

          {dimensions_y.map((row_item, row_index) => (
            <tr key={row_index}>
              <td
                className={`font-family text-md border px-5 font-semibold text-gray-05`}
              >
                <DimensionHeader
                  attribute={attribute_y}
                  rangeStart={row_item[0]}
                  rangeEnd={row_item[1]}
                />
              </td>

              {dimensions_x.map((_, col_index) =>
                Object.entries(cellMetrics).map((metric, metricIndex) => (
                  <td
                    key={`${row_index}-${col_index}-${metricIndex}`}
                    className="border-r border-t"
                  >
                    <CellMetric
                      metric={metric}
                      rowIndex={row_index}
                      colIndex={col_index}
                      value={getCellValue(metric, row_index, col_index)}
                      isEditable={isEditable}
                      onChange={onChangeCellValue}
                    />
                  </td>
                ))
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

RegimeCellViewer.propTypes = {
  matrixInd: PropTypes.number.isRequired,
  isEditable: PropTypes.bool.isRequired,
  cellMetrics: PropTypes.object.isRequired,
  isDisplay: PropTypes.bool.isRequired,
  curMatrix: PropTypes.object.isRequired,
};

export default memo(RegimeCellViewer);
